"use client";

import { useState } from "react";
import {
  MessageCircle,
  HelpCircle,
  Shield,
  Copy,
  History,
  Youtube,
  Pencil,
  Users,
} from "lucide-react";
import Link from "next/link";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useNotificationContext } from "@/contexts/NotificationContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import LogoutButton from "@/components/LogoutButton";
import { EmailModal } from "@/components/layout/EmailModal";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAccountEmail, updateAccountEmail } from "@/lib/api/batchfinder";

interface NavSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (path: string) => void;
  trigger: React.ReactNode;
}

const NavSheet = ({
  isOpen,
  onOpenChange,
  onNavigate,
  trigger,
}: NavSheetProps) => {
  const { pips } = useNotificationContext();
  const { accountId, sessionId } = useAuth();
  const queryClient = useQueryClient();
  const [isEmailModalOpen, setEmailModalOpen] = useState(false);
  const [modalServerError, setModalServerError] = useState<string | null>(null);

  const {
    data: email,
    isLoading: isEmailLoading,
    isFetching: isEmailFetching,
    isError: isEmailError,
    error: emailError,
    refetch: refetchEmail,
  } = useQuery<string | null, Error>({
    queryKey: ["account-email", sessionId],
    enabled: Boolean(sessionId),
    queryFn: async () => {
      if (!sessionId) {
        return null;
      }
      const response = await getAccountEmail(sessionId);
      if (response.statusCode !== 200) {
        const message =
          typeof response.body === "string"
            ? response.body
            : response.body?.message ?? "Unable to load email.";
        throw new Error(message);
      }

      if (
        response.body &&
        typeof response.body === "object" &&
        "email" in response.body
      ) {
        const emailValue = (response.body as { email?: string | null }).email;
        return emailValue ?? null;
      }

      return null;
    },
  });

  const updateEmailMutation = useMutation({
    mutationFn: async (newEmail: string) => {
      if (!sessionId) {
        throw new Error("Missing session. Please log in again.");
      }

      const response = await updateAccountEmail(sessionId, newEmail);
      if (response.statusCode !== 200) {
        const message =
          typeof response.body === "string"
            ? response.body
            : response.body?.message ?? "Failed to update email.";
        throw new Error(message);
      }

      return response;
    },
    onSuccess: () => {
      if (sessionId) {
        queryClient.invalidateQueries({
          queryKey: ["account-email", sessionId],
        });
      }
      toast.success("Email saved.");
      setEmailModalOpen(false);
      setModalServerError(null);
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update email. Please try again.";
      setModalServerError(message);
      toast.error(message);
    },
  });

  const handleEmailSubmit = async (newEmail: string) => {
    setModalServerError(null);
    try {
      await updateEmailMutation.mutateAsync(newEmail);
    } catch {
      // error handled in mutation onError
    }
  };

  const handleEmailModalClose = () => {
    if (updateEmailMutation.isPending) return;
    setEmailModalOpen(false);
    setModalServerError(null);
  };

  // Helper function to render pip
  const renderPip = (tab: keyof typeof pips) => {
    const pipValue = pips[tab];
    if (!pipValue || (typeof pipValue === "number" && pipValue === 0))
      return null;

    return (
      <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
        {typeof pipValue === "number" ? pipValue : ""}
      </div>
    );
  };

  // Copy to clipboard function
  const copyToClipboard = async (text: string, label: string) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success(`${label} copied to clipboard`);
      }
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy to clipboard");
    }
  };

  // Logout should not close the sheet; handled within LogoutButton

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent
          side="left"
          className="w-80"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>More Options</SheetTitle>
          </SheetHeader>

          {/* User Info Section */}
          <div className="mt-4 space-y-2 pb-4 border-b">
            {/* Account ID */}
            {accountId && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">
                  Account ID
                </h3>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-mono truncate">{accountId}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(accountId, "Account ID")}
                    className="h-8 px-3 flex items-center gap-2"
                  >
                    <span className="text-xs">Copy ID</span>
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
            )}

            {sessionId && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">
                  Account Email
                </h3>
                {isEmailLoading ? (
                  <p className="text-sm text-muted-foreground">Loading...</p>
                ) : isEmailError ? (
                  <div className="space-y-2">
                    <p className="text-sm text-red-500">
                      {emailError?.message ?? "Failed to load email."}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => refetchEmail()}
                      disabled={isEmailFetching}
                    >
                      {isEmailFetching ? "Retrying..." : "Retry"}
                    </Button>
                  </div>
                ) : email ? (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-mono break-all">{email}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0 shadow-none hover:shadow-none focus:shadow-none"
                      onClick={() => setEmailModalOpen(true)}
                    >
                      <Pencil className="h-4 w-4" />
                      <span className="sr-only">Update Email</span>
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm text-muted-foreground">
                      No email on file.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEmailModalOpen(true)}
                    >
                      Set Email
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation Links */}
          <nav className="mt-4 space-y-2">
            <button
              className="w-full flex items-center space-x-3 p-3 rounded-lg transition-all active:scale-95"
              onClick={() => onNavigate("/stats")}
            >
              <History size={20} />
              <span>Batch History</span>
            </button>

            <button
              className="w-full flex items-center space-x-3 p-3 rounded-lg transition-all active:scale-95"
              onClick={() => onNavigate("/license")}
            >
              <Shield size={20} />
              <span>Manage License</span>
            </button>

            <button
              className="w-full flex items-center space-x-3 p-3 rounded-lg transition-all active:scale-95"
              onClick={() => {
                onNavigate("/referral");
                onOpenChange(false);
              }}
            >
              <Users size={20} />
              <span>Invite Friends</span>
            </button>

            <button
              className="relative w-full flex items-center space-x-3 p-3 rounded-lg transition-all active:scale-95"
              onClick={() => onNavigate("/support")}
            >
              <MessageCircle size={20} />
              <span>Support & Community Chats</span>
              {renderPip("chat")}
            </button>

            <button
              className="w-full flex items-center space-x-3 p-3 rounded-lg transition-all active:scale-95"
              onClick={() => onNavigate("/help/video")}
            >
              <Youtube size={20} />
              <span>Setup Video</span>
            </button>

            <button
              className="w-full flex items-center space-x-3 p-3 rounded-lg transition-all active:scale-95"
              onClick={() => onNavigate("/help")}
            >
              <HelpCircle size={20} />
              <span>Helpful Articles</span>
            </button>
          </nav>

          {/* Logout Button */}
          <div className="mt-auto pt-6 border-t">
            <LogoutButton />

            {/* Terms of Service Disclaimer */}
            <div className="text-center mt-4">
              <p className="text-xs text-muted-foreground">
                By using BatchFinder, you agree to our{" "}
                <Link
                  href="/terms-of-service"
                  className="text-primary hover:underline"
                >
                  Terms of Service
                </Link>
              </p>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <EmailModal
        isOpen={isEmailModalOpen}
        initialEmail={email ?? null}
        isSaving={updateEmailMutation.isPending}
        serverError={modalServerError}
        onClose={handleEmailModalClose}
        onSubmit={handleEmailSubmit}
      />
    </>
  );
};

export default NavSheet;
